import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Form, Input, Select, Button, Space, message } from 'antd';
import { SaveOutlined, CloseOutlined, GiftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import PersianDatePicker from '../../../components/PersianDatePicker';
import { holidaysApi, departmentsApi, designationsApi } from '../../../utils/api';
import moment from 'moment-jalaali';
import { scrollToFirstError } from '../../../utils/formHelpers';
import { FormSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';

const { Option } = Select;
const { TextArea } = Input;

const EditHoliday = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBackToList = () =>
    navigate(returnToRef.current || '/hr/holidays', { state: buildGoBackState() });
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [deptOptions, setDeptOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);

  const parseArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const formatDate = (val) => {
    if (!val) return null;
    // اگر رشته جلالی باشد
    if (typeof val === 'string') {
      const m = moment(val, 'jYYYY/jMM/jDD');
      if (!m.isValid()) return null;
      return m.format('YYYY-MM-DD');
    }
    // اگر DateObject یا Moment باشد
    const dateObj = val?.toDate ? val.toDate() : val;
    const m = moment(dateObj);
    if (!m.isValid()) return null;
    return m.format('YYYY-MM-DD');
  };

  const toJalaliString = (dateStr) => {
    const m = moment(dateStr);
    if (!m.isValid()) return null;
    return m.format('jYYYY/jMM/jDD');
  };

  const loadData = async () => {
    setInitialLoading(true);
    try {
      const [holidayRes, deptRes, desRes] = await Promise.all([
        holidaysApi.getById(id),
        departmentsApi.getAll(),
        designationsApi.getAll(),
      ]);
      setDeptOptions(deptRes.data || []);
      setDesignationOptions(desRes.data || []);

      const data = holidayRes.data || {};
      const departments = parseArray(data.department_id_json).map(String);
      const designations = parseArray(data.designation_id_json).map(String);
      const employmentTypes = parseArray(data.employment_type_json);

      form.setFieldsValue({
        date: data.date ? toJalaliString(data.date) : null,
        occasion: data.occassion || data.occasion || '',
        department: departments,
        jobTitle: designations,
        employmentType: employmentTypes,
      });
    } catch (error) {
      console.error('خطا در دریافت اطلاعات تعطیلی:', error);
      message.error('خطا در دریافت اطلاعات تعطیلی');
    } finally {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleSubmit = async (values) => {
    try {
      const validatedValues = await form.validateFields();
      setLoading(true);
      const payload = {
        occassion: validatedValues.occasion || '',
        date: formatDate(validatedValues.date),
        department: validatedValues.department ? validatedValues.department.map(Number) : [],
        designation: validatedValues.jobTitle ? validatedValues.jobTitle.map(Number) : [],
        employment_type: validatedValues.employmentType || [],
      };
      await holidaysApi.update(id, payload);
      message.success('تعطیلی با موفقیت ویرایش شد');
      goBackToList();
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) {
        scrollToFirstError(form);
      } else {
        message.error('خطا در ویرایش تعطیلی');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => goBackToList();

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <GiftOutlined className="text-amber-600 dark:text-amber-400 text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0">ویرایش تعطیلی</h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm m-0">ویرایش تعطیلی با شناسه #{id}</p>
              </div>
            </div>
            <Space>
              <Button icon={<ArrowRightOutlined />} onClick={handleCancel} style={{ fontFamily: 'CustomFont' }}>
                بازگشت
              </Button>
            </Space>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            {initialLoading ? (
              <FormSkeleton />
            ) : (
              <Card className="shadow-sm border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                <Form form={form} layout="vertical" onFinish={handleSubmit} scrollToFirstError={{ behavior: 'smooth', block: 'center' }} style={{ fontFamily: 'CustomFont' }}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                      label={
                        <span>
                          تاریخ
                        </span>
                      }
                      name="date"
                      rules={[{ required: true, message: 'لطفاً تاریخ را انتخاب کنید' }]}
                    >
                      <PersianDatePicker style={{ width: '100%' }} placeholder="انتخاب تاریخ" />
                    </Form.Item>

                    <Form.Item label="مناسبت" name="occasion">
                      <TextArea rows={2} placeholder="مناسبت" style={{ fontFamily: 'CustomFont' }} />
                    </Form.Item>

                    <Form.Item label="دپارتمان" name="department">
                      <Select
                        mode="multiple"
                        placeholder="انتخاب دپارتمان"
                        allowClear
                        style={{ fontFamily: 'CustomFont' }}
                        loading={!deptOptions.length}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          if (option?.value === '__all__') return true;
                          return (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
                        }}
                        onChange={(values) => {
                          if (values.includes('__all__')) {
                            form.setFieldsValue({ department: deptOptions.map((d) => String(d.id)) });
                          }
                        }}
                      >
                        <Option value="__all__"><span style={{ fontWeight: 'bold', color: '#1890ff' }}>— انتخاب همه —</span></Option>
                        {deptOptions.map((d) => (
                          <Option key={d.id} value={String(d.id)}>
                            {d.team_name || d.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="عنوان شغلی" name="jobTitle">
                      <Select
                        mode="multiple"
                        placeholder="انتخاب عنوان شغلی"
                        allowClear
                        style={{ fontFamily: 'CustomFont' }}
                        loading={!designationOptions.length}
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          if (option?.value === '__all__') return true;
                          return (option?.children ?? '').toLowerCase().includes(input.toLowerCase());
                        }}
                        onChange={(values) => {
                          if (values.includes('__all__')) {
                            form.setFieldsValue({ jobTitle: designationOptions.map((d) => String(d.id)) });
                          }
                        }}
                      >
                        <Option value="__all__"><span style={{ fontWeight: 'bold', color: '#1890ff' }}>— انتخاب همه —</span></Option>
                        {designationOptions.map((d) => (
                          <Option key={d.id} value={String(d.id)}>
                            {d.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Form.Item label="نوع استخدام" name="employmentType">
                      <Select
                        mode="multiple"
                        placeholder="انتخاب نوع استخدام"
                        allowClear
                        style={{ fontFamily: 'CustomFont' }}
                        onChange={(values) => {
                          if (values.includes('__all__')) {
                            form.setFieldsValue({ employmentType: ['fulltime', 'parttime', 'contract', 'intern', 'training', 'full_time', 'part_time'] });
                          }
                        }}
                      >
                        <Option value="__all__"><span style={{ fontWeight: 'bold', color: '#1890ff' }}>— انتخاب همه —</span></Option>
                        <Option value="fulltime">تمام وقت</Option>
                        <Option value="parttime">پاره وقت</Option>
                        <Option value="contract">قراردادی</Option>
                        <Option value="intern">کارآموزی</Option>
                        <Option value="training">آموزشی</Option>
                        <Option value="full_time">تمام وقت</Option>
                        <Option value="part_time">پاره وقت</Option>
                      </Select>
                    </Form.Item>
                  </div>

                  <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-gray-700 mt-6">
                    <Button icon={<CloseOutlined />} onClick={handleCancel} size="middle" style={{ fontFamily: 'CustomFont' }}>
                      لغو
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      htmlType="submit"
                      loading={loading}
                      size="middle"
                      className="bg-green-600 hover:bg-green-700 border-green-600"
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      ذخیره تغییرات
                    </Button>
                  </div>
                </Form>
              </Card>
            )}
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default EditHoliday;
